import {sendEvent} from '@github-ui/hydro-analytics'
import {SidebarCollapseIcon, SidebarExpandIcon} from '@primer/octicons-react'
import {ActionList, IconButton} from '@primer/react'
import {type MouseEvent, type RefObject, useCallback} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {useActivityPanel} from '../timeline/ActivityPanelContext'

type ActivityPanelToggleProps = {
  className?: string
}

/**
 * Opens or closes the Activity panel from the header, mirroring the behavior of the "Activity"
 * button rendered above the timeline. Only mount this when `showActivityTab` is true. The panel
 * content comes from `TimelineActivityMenu`, which only mounts in that case, so opening the panel
 * without it shows an empty docked sidebar.
 */
export function ActivityPanelToggle({className}: ActivityPanelToggleProps) {
  const {open, openPanel, closePanel} = useActivityPanel()

  const handleToggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (open) {
        closePanel()
        sendEvent('issue_viewer.activity_panel.closed', {source: 'header_button'})
      } else {
        openPanel({opener: event.currentTarget})
        sendEvent('issue_viewer.activity_panel.opened', {source: 'header_button'})
      }
    },
    [open, openPanel, closePanel],
  )

  const ariaLabel = open ? BUTTON_LABELS.closeActivityPanel : BUTTON_LABELS.openActivityPanel

  return (
    <IconButton
      icon={open ? SidebarCollapseIcon : SidebarExpandIcon}
      aria-label={ariaLabel}
      aria-expanded={open}
      variant="invisible"
      size="medium"
      tooltipDirection="s"
      className={className}
      onClick={handleToggle}
    />
  )
}

type ActivityPanelMenuItemProps = {
  /**
   * The overflow menu's own trigger button. Selecting a menu item closes the menu, and Primer
   * returns focus to this anchor first, so it's the correct return-focus target for the Activity
   * panel once it's later dismissed.
   */
  anchorRef: RefObject<HTMLElement | null>
}

/**
 * Overflow-menu equivalent of `ActivityPanelToggle`, for compact headers that collapse actions
 * into a kebab menu. Keeps Activity's entry point consistent with Copy link and New issue, which
 * both render as menu items in the same collapsed state.
 */
export function ActivityPanelMenuItem({anchorRef}: ActivityPanelMenuItemProps) {
  const {open, openPanel, closePanel} = useActivityPanel()

  const handleSelect = useCallback(() => {
    if (open) {
      closePanel()
      sendEvent('issue_viewer.activity_panel.closed', {source: 'header_menu'})
    } else {
      openPanel({opener: anchorRef.current ?? undefined})
      sendEvent('issue_viewer.activity_panel.opened', {source: 'header_menu'})
    }
  }, [open, openPanel, closePanel, anchorRef])

  const Icon = open ? SidebarCollapseIcon : SidebarExpandIcon
  const label = open ? BUTTON_LABELS.closeActivityPanel : BUTTON_LABELS.openActivityPanel

  return (
    <ActionList.Item onSelect={handleSelect}>
      <ActionList.LeadingVisual>
        <Icon />
      </ActionList.LeadingVisual>
      {label}
    </ActionList.Item>
  )
}
